import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchYingshiDeviceList } from '@/redux/yingshi/deviceListSlice';
import { mapYingshiDevicesToMergedList } from '@/views/ys/mergeAccountAndTrustDevices';
import { renderPhoneStr, renderTextStr } from '@/utils/renderUtils';

function deviceKey(device) {
  return `${device.deviceSerial}_${device.channelNo}`;
}

function DeviceStatus({ storeId }) {
  const dispatch = useDispatch();
  const yingshiDeviceList = useSelector((state) => state.yingshi.deviceList);
  const { data, query } = yingshiDeviceList;
  const scrollRef = useRef(null);
  const posRef = useRef(0);
  const rafRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [openKey, setOpenKey] = useState('');

  useEffect(() => {
    const id = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';
    if (!id) return undefined;
    dispatch(fetchYingshiDeviceList({ query: { storeId: id } })).unwrap().catch(() => {});
    return undefined;
  }, [dispatch, storeId]);

  const devices = useMemo(() => {
    const id = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';
    if (!id || String(query?.storeId ?? '') !== id) return [];
    return mapYingshiDevicesToMergedList(data);
  }, [data, query, storeId]);

  const openDevice = devices.find((device) => deviceKey(device) === openKey) || null;

  useEffect(() => {
    posRef.current = 0;
    if (scrollRef.current) {
      scrollRef.current.style.transform = 'translateY(0px)';
    }
  }, [devices]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const halfHeight = el.scrollHeight / 2;
    const speed = 0.15;

    const animate = () => {
      if (!paused && !openKey && halfHeight > 0) {
        posRef.current += speed;
        if (posRef.current >= halfHeight) {
          posRef.current -= halfHeight;
        }
        el.style.transform = `translateY(${-posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused, openKey, devices]);

  const renderItem = (device, idx) => {
    const isOnline = device.status === 1 || device.status === '1';
    const mode = device.isTrustDevice === true || device.isTrustDevice === 1 ? '托管' : '账号';
    const mode2 = device.needInference === true ? '推理中' : '暂停推理';
    const room = (device.location && String(device.location).trim()) || device.name || '--';
    return (
      <div
        className="ds-item"
        key={`${device.deviceSerial}_${device.channelNo}-${idx}`}
        onClick={() => setOpenKey(deviceKey(device))}
      >
        <span className="ds-room">{room}</span>
        <span className="ds-id">{device.deviceSerial}</span>
        <span className="ds-channel">{`通道 ${device.channelNo}`}</span>
        <span className={`ds-status ${isOnline ? 'ds-online' : 'ds-offline'}`}>
          {isOnline ? '在线' : '离线'}
        </span>
        <span className={`ds-mode ds-${mode}`}>{mode}</span>
        <span className={`ds-mode2 ds-${mode2}`}>{mode2}</span>
      </div>
    );
  };

  return (
    <div className="device-status-card">
      <div className="ds-header">
        <span className="ds-title">在线设备</span>
        <span className="ds-count">
          <span className="ds-count-num">
            {devices.filter((d) => d.status === 1 || d.status === '1').length}
          </span>
          <span className="ds-count-unit">台</span>
        </span>
      </div>
      <div className="ds-cols">
        <span className="ds-col">育婴室</span>
        <span className="ds-col">设备ID</span>
        <span className="ds-col">通道</span>
        <span className="ds-col">状态</span>
        <span className="ds-col">模式</span>
        <span className="ds-col">推理</span>
      </div>
      <div
        className="ds-scroll-wrap"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="ds-scroll" ref={scrollRef}>
          {devices.map(renderItem)}
          {devices.length > 0 ? devices.map((device, idx) => renderItem(device, `dup-${idx}`)) : null}
        </div>
      </div>
      {openDevice && createPortal(
        <div className="ds-modal-overlay" onClick={() => setOpenKey('')}>
          <div className="ds-modal ds-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="ds-side">
              <div className="ds-side-title">在线设备</div>
              <div className="ds-side-wrap">
                <div className="ds-side-scroll">
                  {devices.map((device) => {
                    const online = device.status === 1 || device.status === '1';
                    const room = (device.location && String(device.location).trim()) || device.name || '--';
                    return (
                      <div
                        key={deviceKey(device)}
                        className={`ds-side-item${deviceKey(device) === openKey ? ' active' : ''}`}
                        onClick={() => setOpenKey(deviceKey(device))}
                      >
                        <div className="ds-side-top">
                          <span className="ds-side-room">{room}</span>
                          <span className="ds-side-id">{device.deviceSerial}</span>
                          <span className={`ds-status ${online ? 'ds-online' : 'ds-offline'}`}>{online ? '在线' : '离线'}</span>
                          <span className="ds-side-brand">{renderTextStr(device.brand) || '--'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="ds-main">
              <div className="ds-modal-header">
                <span className="ds-modal-title">
                  {(openDevice.location && String(openDevice.location).trim()) || openDevice.name || '设备'}
                </span>
                <button type="button" className="ds-modal-close" onClick={() => setOpenKey('')} title="关闭">×</button>
              </div>
              <div className="ds-modal-body">
                <table className="ds-modal-table">
                  <tbody>
                    <tr>
                      <td className="ds-modal-label">序列号</td>
                      <td className="ds-modal-value mono">{openDevice.deviceSerial}</td>
                      <td className="ds-modal-label">通道号</td>
                      <td className="ds-modal-value">{openDevice.channelNo}</td>
                    </tr>
                    <tr>
                      <td className="ds-modal-label">门店</td>
                      <td className="ds-modal-value">{renderTextStr(openDevice.storeName)}</td>
                      <td className="ds-modal-label">品牌</td>
                      <td className="ds-modal-value">{renderTextStr(openDevice.brand)}</td>
                    </tr>
                    <tr>
                      <td className="ds-modal-label">安装位置</td>
                      <td className="ds-modal-value">{renderTextStr(openDevice.location)}</td>
                      <td className="ds-modal-label">床号</td>
                      <td className="ds-modal-value">{openDevice.bedNo != null && openDevice.bedNo !== '' ? String(openDevice.bedNo) : '--'}</td>
                    </tr>
                    <tr>
                      <td className="ds-modal-label">通知手机</td>
                      <td className="ds-modal-value">{renderPhoneStr(openDevice.phone || openDevice.notifyPhone || openDevice.mobile)}</td>
                      <td className="ds-modal-label">置顶</td>
                      <td className="ds-modal-value">{openDevice.isPinned === true || openDevice.isPinned === 1 ? '是' : '否'}</td>
                    </tr>
                    <tr>
                      <td className="ds-modal-label">推理</td>
                      <td className={`ds-modal-value ds-ai-${openDevice.needInference === true ? '开启' : '关闭'}`}>
                        {openDevice.needInference === true ? '开启' : '关闭'}
                      </td>
                      <td className="ds-modal-label">排序</td>
                      <td className="ds-modal-value">{openDevice.sort != null && openDevice.sort !== '' ? String(openDevice.sort) : '--'}</td>
                    </tr>
                    <tr>
                      <td className="ds-modal-label">来源</td>
                      <td className="ds-modal-value">{openDevice.isTrustDevice === true || openDevice.isTrustDevice === 1 ? '托管' : '账号'}</td>
                      <td className="ds-modal-label">是否在线</td>
                      <td className="ds-modal-value" colSpan={1}>
                        <span className={`ds-modal-online-badge ${(openDevice.status === 1 || openDevice.status === '1') ? 'online' : 'offline'}`}>
                          <span className="ds-modal-online-dot"></span>
                          {(openDevice.status === 1 || openDevice.status === '1') ? '在线' : '离线'}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DeviceStatus;
